import { jsx as _jsx } from "react/jsx-runtime";
/**
 * KeyboardShortcut — small inline keycap badge.
 *
 * Renders one or more keys as a single semantic `<kbd>` containing
 * individual key glyphs. Use it as a shortcut hint inline next to a
 * label, button, or active tab.
 *
 *   <KeyboardShortcut keys={["Q"]} />
 *   <KeyboardShortcut keys={["⌘", "K"]} size="regular" />
 *
 * Two sizes:
 *   - `mini`     — compact badge for inline hints (e.g. inside a tab pill).
 *   - `regular`  — standalone affordance (e.g. omnibar's `⌘K search`).
 *
 * Tone is neutral by default; set `tone="dark"` when rendering on a dark
 * surface (e.g. tooltip bubble) so the keycap reads as a quiet inset chip
 * instead of a bright label.
 */
import React from "react";
import { memoForwardRef } from "@shared/client/util/memo";
import "./keyboard-shortcut.scss";
export const KeyboardShortcut = memoForwardRef(({ keys, size = "regular", tone = "light", className, ...rest }, ref) => {
    const ariaLabel = rest["aria-label"] ?? `Shortcut: ${keys.join(" ")}`;
    const cls = [
        "keyboard-shortcut",
        `keyboard-shortcut--${size}`,
        `keyboard-shortcut--${tone}`,
        className,
    ]
        .filter(Boolean)
        .join(" ");
    return (_jsx("kbd", { ref: ref, className: cls, "aria-label": ariaLabel, children: keys.map((key, i) => (_jsx("span", { className: "keyboard-shortcut__key", children: key }, i))) }));
});
KeyboardShortcut.displayName = "KeyboardShortcut";
